import React from "react";
import { Clock, StickyNote } from "lucide-react";

const GG = [["lun", "Lun"], ["mar", "Mar"], ["mer", "Mer"], ["gio", "Gio"], ["ven", "Ven"], ["sab", "Sab"], ["dom", "Dom"]];
const fD = (s) => (s ? String(s).slice(0, 10).split("-").reverse().join("/") : "");
const SING = { compresse: "compressa", capsule: "capsula", bustine: "bustina", supposte: "supposta", fiale: "fiala", cerotti: "cerotto" };
export const qtaTxt = (q) => { const m = String(q || "").trim().match(/^(1|½|¼|¾|0[.,]\d+|1\/2|1\/4|3\/4)\s+(\S+)(.*)$/); return m && SING[m[2]] ? `${m[1]} ${SING[m[2]]}${m[3]}` : String(q || ""); };
export const giorniTxt = (t) => { const g = t.giorni || []; if (!g.length || g.length === 7) return ""; const q = t.quantita_giorni || {}; return GG.filter(([c]) => g.includes(c)).map(([c, l]) => (q[c] ? `${l} ×${q[c]}` : l)).join(", "); };
const orariDi = (t) => [...new Set((Array.isArray(t.orari) ? t.orari : String(t.orari || "").split(",")).map((x) => String(x).trim()).filter((x) => /\d/.test(x)))];
export const alBisogno = (t) => t.tipo === "bisogno" || (!orariDi(t).length && /bisogno/i.test(String(t.orari || "")));
const GL = { lun: "Lunedì", mar: "Martedì", mer: "Mercoledì", gio: "Giovedì", ven: "Venerdì", sab: "Sabato", dom: "Domenica" };
const aGiorni = (t) => !t.ogni_giorni && (t.giorni || []).length > 0 && t.giorni.length < 7;
const schemaDi = (t) => {
  if (t.schema_giorni && Object.keys(t.schema_giorni).length) return t.schema_giorni;
  const q = t.quantita_giorni || {};
  return Object.fromEntries(t.giorni.map((c) => [c, (orariDi(t).length ? orariDi(t) : [""]).map((o) => ({ ora: o, qta: q[c] || t.quantita || "" }))]));
};

function PerGiorno({ items, onOpen, extra, scope }) {
  const righe0 = GG.map(([c]) => [c, items.flatMap((t) => (schemaDi(t)[c] || []).map((e) => ({ t, e }))).sort((a, b) => (a.e.ora || "99").localeCompare(b.e.ora || "99"))]).filter(([, l]) => l.length);
  const gruppi = {};
  righe0.forEach(([c, l]) => { const k = JSON.stringify(l.map(({ t, e }) => [t.id || t.farmaco, e.ora, e.dose, e.qta])); (gruppi[k] = gruppi[k] || { giorni: [], l }).giorni.push(c); });
  const righe = Object.values(gruppi).map((g) => [g.giorni[0], g.l, g.giorni]);
  const visti = {};
  righe.forEach(([c, l]) => l.forEach(({ t, e }, i) => { const k = `${t.id || t.farmaco}|${e.ora}`; if (!(k in visti)) visti[k] = `${c}-${i}`; }));
  const primo = (t, e, c, i) => visti[`${t.id || t.farmaco}|${e.ora}`] === `${c}-${i}`;
  return (
    <div className="mt-2 pt-2 border-t" data-testid={`tvo-giorni-${scope}`}>
      <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 mb-1">Solo alcuni giorni</div>
      {righe.map(([c, l, gg]) => (
        <div key={c} className="flex items-start gap-2 py-1.5 border-b last:border-0" data-testid={`tvo-giorno-${c}-${scope}`}>
          <div className="text-emerald-700 font-extrabold text-xs shrink-0 w-[4.4rem] pt-0.5 space-y-0.5">{gg.map((x) => <div key={x}>{GL[x]}</div>)}</div>
          <div className="min-w-0 flex-1 space-y-1.5">{l.map(({ t, e }, i) => (
            <div key={i} className="flex items-start gap-1.5"><span className="inline-flex items-center gap-0.5 text-primary font-bold text-xs tabnum shrink-0 pt-px"><Clock size={11} />{e.ora || "—"}</span>
              <div className="min-w-0 flex-1"><MedRow t={{ ...t, dosaggio: e.dose || t.dosaggio, quantita: e.qta, giorni: [], quantita_giorni: {} }} orig={t} ora={e.ora} onOpen={onOpen} extra={extra && primo(t, e, c, i) ? extra : undefined} /></div></div>))}</div>
        </div>
      ))}
    </div>
  );
}
const byTime = (items) => {
  const map = {}; const noTime = [];
  items.forEach((t) => { const tm = orariDi(t); if (!tm.length) noTime.push(t); else tm.forEach((x) => { (map[x] = map[x] || []).push(t); }); });
  return { keys: Object.keys(map).sort(), map, noTime };
};

function MedRow({ t, onOpen, extra, orig, ora }) {
  const dose = t.dosaggio || t.dose;
  const info = [t.insulina ? "insulina" : "", t.via, t.pasti, t.modalita, t.ogni_giorni ? `ogni ${t.ogni_giorni} gg` : "", t.dal || t.al ? `${t.dal ? `dal ${fD(t.dal)}` : ""}${t.al ? ` al ${fD(t.al)}` : " · senza fine"}` : ""].filter(Boolean);
  const Tag = onOpen ? "button" : "div";
  return (
    <div className="group flex flex-wrap sm:flex-nowrap items-start justify-between gap-1.5" data-testid={`tvo-med-${t.id || t.farmaco}`}>
      <Tag type={onOpen ? "button" : undefined} onClick={onOpen ? () => onOpen(orig || t) : undefined} className="min-w-0 text-left flex-1 basis-full sm:basis-auto" data-testid={onOpen ? `tvo-open-${t.id}` : undefined}>
        <div className="break-words">
          <span className="text-xs font-semibold">{t.farmaco}</span>
          {t.generico && <span className="text-[11px] italic text-muted-foreground"> ({t.generico})</span>}
          {dose && <span className="text-[11px] text-muted-foreground"> · {dose}</span>}
          {t.quantita && !giorniTxt(t) && <span className="text-[11px] text-sky-700 font-medium"> · Q.tà {qtaTxt(t.quantita)}</span>}
          {info.map((x) => <span key={x} className="text-[11px] text-muted-foreground"> · {x}</span>)}
          {giorniTxt(t) && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 rounded px-1 py-0.5 ml-1">{giorniTxt(t)}</span>}
        </div>
        {t.note && String(t.note).trim() && <div className="mt-0.5 flex items-start gap-1 text-[10px] text-amber-800"><StickyNote size={9} className="mt-0.5 shrink-0" /><span className="line-clamp-2">{t.note}</span></div>}
      </Tag>
      {extra && extra(orig || t, ora)}
    </div>
  );
}

function Bisogno({ list, onOpen, extra }) {
  return (
    <div className="mb-1" data-testid="tvo-bisogno">
      <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700 mb-1">Al bisogno</div>
      {list.length === 0 ? <span className="text-[11px] text-muted-foreground">Nessuna terapia al bisogno</span>
        : <div className="space-y-1">{[...list].sort((a, b) => (a.farmaco || "").localeCompare(b.farmaco || "")).map((t) => <div key={t.id || t.farmaco} className="rounded bg-amber-50 border border-amber-100 px-2 py-1"><MedRow t={t} onOpen={onOpen} extra={extra} /></div>)}</div>}
    </div>
  );
}

export function TerapiaOrari({ terapie, onOpen, extra, scope = "x", bisogno = true, ordinaria = true, empty = "Nessuna terapia quotidiana" }) {
  const bis = terapie.filter(alBisogno);
  const spec = terapie.filter((t) => !alBisogno(t) && aGiorni(t));
  const { keys, map, noTime } = byTime(terapie.filter((t) => !alBisogno(t) && !aGiorni(t)));
  return (
    <div data-testid={`tvo-${scope}`}>
      {bisogno && (bis.length > 0 || ordinaria === false) && <Bisogno list={bis} onOpen={onOpen} extra={extra} />}
      {ordinaria && <div className={bisogno && bis.length ? "mt-2 pt-2 border-t" : ""}>
        {keys.length === 0 && noTime.length === 0 && !spec.length && <p className="text-xs text-muted-foreground py-1">{empty}</p>}
        {(keys.length > 0 || noTime.length > 0) && <div className="text-[10px] font-bold uppercase tracking-wide text-primary mb-1" data-testid={`tvo-tutti-${scope}`}>Tutti i giorni</div>}
        {keys.map((tm) => (
          <div key={tm} data-testid={`orario-${tm}-${scope}`} className="flex items-start gap-2 py-1.5 border-b last:border-0">
            <div className="flex items-center gap-1 text-primary font-extrabold text-sm tabnum shrink-0 w-[4.4rem] pt-0.5"><Clock size={13} className="shrink-0" /> {tm}</div>
            <div className="min-w-0 flex-1 space-y-1.5">{map[tm].map((t) => <MedRow key={t.id || t.farmaco} t={t} ora={tm} onOpen={onOpen} extra={extra} />)}</div>
          </div>
        ))}
        {noTime.length > 0 && (
          <div className="flex items-start gap-2 py-1.5">
            <div className="text-[10px] font-bold text-muted-foreground shrink-0 w-[4.4rem] pt-1">SENZA ORA</div>
            <div className="min-w-0 flex-1 space-y-1.5">{noTime.map((t) => <MedRow key={t.id || t.farmaco} t={t} onOpen={onOpen} extra={extra} />)}</div>
          </div>
        )}
        {spec.length > 0 && <PerGiorno items={spec} onOpen={onOpen} extra={extra} scope={scope} />}
      </div>}
    </div>
  );
}
